import type { AlertInstance } from '../api/types';

const n1 = (v: unknown) => (typeof v === 'number' ? v.toFixed(1) : String(v ?? '—'));
const n0 = (v: unknown) => (typeof v === 'number' ? v.toFixed(0) : String(v ?? '—'));
const days = (v: unknown) => `${n0(v)} day${v === 1 ? '' : 's'}`;
const words = (v: unknown) => String(v ?? '').replace(/_/g, ' ');

/**
 * The one-sentence headline of an alert as plain text ("No wellbeing check-in for 9 days."),
 * for lists that show it on a single line. The detail view builds the same sentences as JSX in
 * AlertPayloadDetail; keep the two in step.
 */
export function alertSentence(alert: Pick<AlertInstance, 'rule_code' | 'rule_name' | 'payload'>): string {
  const p = alert.payload as Record<string, any>;
  switch (alert.rule_code) {
    case 'academic_decline_subject':
      return `${p.subject ?? 'Subject'} average dropped ${n1(p.delta_pct)}% (from ${n1(p.prior_avg)}% to ${n1(p.current_score)}%).`;
    case 'repeated_chapter_fail': {
      const scores: number[] = Array.isArray(p.recent_scores) ? p.recent_scores : [];
      return `${p.chapter_name ?? 'Chapter'} — ${p.consecutive_fails ?? scores.length} consecutive fails.`;
    }
    case 'missed_milestone':
      return p.status_at_fire === 'lapsed'
        ? `Commitment lapsed${p.silent_days_observed != null ? ` — silent for ${p.silent_days_observed} days` : ''}.`
        : `No progress for ${p.silent_days_observed ?? 0} days on the active commitment.`;
    case 'wellbeing_red_flag': {
      const states: string[] = Array.isArray(p.consecutive_states) ? p.consecutive_states : [];
      return `Wellbeing red flag: ${states.join(', ')}.`;
    }
    case 'wellbeing_silence':
      return `No wellbeing check-in for ${days(p.days_silent)}.`;
    case 'engagement_drop':
      return p.trigger === 'silent'
        ? `No activity on the app for ${days(p.days_silent)}.`
        : `Study time fell to ${n0(p.recent_minutes)} minutes this week, from a usual ${n0(p.baseline_minutes)}.`;
    case 'late_night_study': {
      const dates: string[] = Array.isArray(p.sample_event_dates) ? p.sample_event_dates : [];
      const n = p.late_nights ?? dates.length;
      return `Studied late at night on ${n} night${n === 1 ? '' : 's'} this week.`;
    }
    case 'peer_gap_persistent':
      return `${p.subject_name ?? 'Subject'}: in the ${p.direction !== 'top' ? 'bottom' : 'top'} 10% of the class for ${p.weeks_in_decile ?? '?'} weeks running.`;
    case 'class_rank_drop':
      return `Class standing fell ${n0(p.drop_pct)} points, from the ${n0(p.baseline_percentile)}th to the ${n0(p.current_percentile)}th percentile.`;
    case 'streak_break':
      return `A ${p.previous_length ?? '?'}-day study streak ended${p.days_missed != null ? `, after ${days(p.days_missed)} missed` : ''}.`;
    case 'study_plan_skipped': {
      const subjects: string[] = Array.isArray(p.subjects_skipped) ? p.subjects_skipped : [];
      const n = p.skipped_count ?? subjects.length;
      return `Skipped ${n} study-plan item${n === 1 ? '' : 's'}.`;
    }
    case 'ai_overreliance':
      return `AI help rose to about ${n1(p.recent_daily)} uses a day (usually ${n1(p.baseline_daily)}) while scores fell.`;
    case 'ai_cheating_suspected':
      return 'AI features were used during an exam.';
    default: {
      const first = Object.entries(p).find(([, v]) => v != null && typeof v !== 'object');
      return first ? `${words(first[0])}: ${String(first[1])}` : alert.rule_name;
    }
  }
}
